import { AppBar, Box, IconButton, Toolbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link } from 'react-router-dom';
import { BrandMark } from '@/shared/ui/BrandMark';
import { useMobileNavOpen } from './useMobileNavOpen';

/**
 * Mobile-only nav-open bar (hamburger + brand mark).
 *
 * On desktop the permanent Drawer is always on screen, so its own header
 * row (logo + collapse toggle, in `AppShell`'s Drawer) is the only chrome
 * needed — there is nothing left for a full-width bar above it to do.
 * Keeping one there anyway used to leave a strip that was, on every page,
 * completely empty except for a tiny hamburger + logo: the "wasted upper
 * header" app.zip's own Topbar (title + actions, no separate shell bar at
 * all — its logo lives in the sidebar) never has. Excluding a global
 * search trigger from that reclaimed space is deliberate too: app.zip's
 * Topbar fills it with a real, working search — Qfly has no global search
 * feature behind one yet, and a decorative box that does nothing is
 * exactly the "wasted header" complaint this component now avoids.
 *
 * On mobile the permanent Drawer becomes a hidden overlay, so *something*
 * fixed on screen has to both open it and orient the user — that bar
 * earns its space there, and this component now renders it only for that
 * viewport (`null` otherwise). The desktop collapse/expand toggle that
 * used to live here moved into the Drawer's own header row, next to the
 * logo — mobile has no rail-collapse concept, so this bar never needs it.
 */
export const TopBar = (): JSX.Element | null => {
  const mobileNav = useMobileNavOpen();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) {
    return null;
  }

  const navToggleLabel = mobileNav.open ? 'Close navigation' : 'Open navigation';

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={1}
      sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={navToggleLabel}>
            <IconButton
              edge="start"
              onClick={mobileNav.toggle}
              aria-label={navToggleLabel}
              aria-expanded={mobileNav.open}
              size="small"
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          <Link
            to="/dashboard"
            style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}
          >
            <BrandMark height={28} title="Qfly CRM" />
          </Link>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
